import { useAuthStore } from '@/stores/authStore'
import { useAuthAdapter } from '../platform/authAdapter'
import { appAuth } from '../platform/appAuth'
import { refreshCoordinator } from '@/services/http/refreshCoordinator'
import { redirectToClientLogin, redirectToClientLogout } from '../redirect/authRedirect'
import { handleAuthSuccess as writeUserDataToStorage } from '@/services/auth/authApi'
import { logger } from '@myorg/type-lib'

/**
 * checkAuth single-flight
 */
let checkAuthPromise: Promise<boolean> | null = null

export const useAuthFacade = () => {
  const authStore = useAuthStore()
  const adapter = useAuthAdapter()

  /**
   * 驗證目前登入狀態
   */
  const checkAuth = async (): Promise<boolean> => {
    // 已登入直接放行
    if (authStore.isLoggedIn) {
      return true
    }

    // 共用驗證請求
    if (checkAuthPromise) {
      return checkAuthPromise
    }

    checkAuthPromise = (async () => {
      try {
        const result = await adapter.checkAuth()

        if (result.success && result.user) {
          authStore.setUser(result.user)
          authStore.setLoginStatus(true)

          return true
        }

        authStore.setUser(null)
        authStore.setLoginStatus(false)

        return false
      } catch {
        authStore.setUser(null)
        authStore.setLoginStatus(false)

        return false
      } finally {
        checkAuthPromise = null
      }
    })()

    return checkAuthPromise
  }

  /**
   * 登入
   */
  const login = async () => {
    await adapter.login()
    // Facade controls redirect
    redirectToClientLogin()
  }

  /**
   * 處理登入成功（統一入口）
   * 用於 OAuth callback 和 deepLink callback
   */
  const handleLoginSuccess = async (tokens: {
    access_token: string
    refresh_token?: string
  }, user: any): Promise<void> => {
    logger.log('[authFacade.handleLoginSuccess] ===== START =====');
    logger.log('[authFacade.handleLoginSuccess] Tokens received:', !!tokens.access_token);
    logger.log('[authFacade.handleLoginSuccess] User data:', user);

    // Persistence layer: appAuth handles tokens, adapter handles user profile
    await appAuth.setTokens(tokens)
    logger.log('[authFacade.handleLoginSuccess] Tokens stored in appAuth');

    await adapter.setUser(user)
    logger.log('[authFacade.handleLoginSuccess] User profile stored in adapter');

    // Write user data to localStorage for compatibility with existing components
    logger.log('[authFacade.handleLoginSuccess] About to call writeUserDataToStorage');
    await writeUserDataToStorage()
    logger.log('[authFacade.handleLoginSuccess] writeUserDataToStorage completed');

    // Runtime layer: store handles in-memory state
    authStore.setUser(user)
    authStore.setLoginStatus(true)
    logger.log('[authFacade.handleLoginSuccess] Store updated');
    logger.log('[authFacade.handleLoginSuccess] ===== END =====');
  }

  /**
   * 真正的 logout 實作
   */
  const doLogout = async (
    options?: { isForce?: boolean }
  ): Promise<void> => {
    // 粉碎 checkAuth 鎖
    checkAuthPromise = null

    // 清除記憶體登入狀態
    authStore.clearAuthData()

    // 清空舊訊息
    authStore.clearMessages()

    // 設定提示訊息
    if (options?.isForce) {
      authStore.setError('登入逾時，請重新登入')
    } else {
      authStore.setMessage('您已登出')
    }

    /**
     * Adapter 處理：
     * - revoke token (app mode)
     * - Preferences 清理 (app mode, including user profile)
     */
    await adapter.logout()

    // Facade controls redirect
    redirectToClientLogout()
  }

  /**
   * logout single-flight
   */
  const logout = async (
    options?: { isForce?: boolean }
  ): Promise<void> => {
    return refreshCoordinator.runLogout(async () => {
      await doLogout(options)
    })
  }

  return {
    checkAuth,
    login,
    logout,
    handleLoginSuccess,

    get user() {
      return authStore.user
    },

    get isLoggedIn() {
      return authStore.isLoggedIn
    }
  }
}